import * as React from "react";
import Button from "@mui/material/Button";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import ProcessVersionsDialog from "./ProcessVersionsDialog";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { useState, useMemo } from "react";
import { useToken } from "../items/UseToken";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import EditIcon from "@mui/icons-material/Edit";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReadOnlyWorkflowViewer from "./ProcessDetailsFlowDialog";

export default function ProcessActionsMenu(props) {
    const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

    // ✅ confirm delete dialog
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    const [anchorEl, setAnchorEl] = React.useState(null);
    const { token } = useToken();

    const open = Boolean(anchorEl);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleVersionsDialogOpen = () => {
        setVersionsDialogOpen(true);
        handleClose();
    };

    const handleDetailsDialogOpen = () => {
        setDetailsDialogOpen(true);
        handleClose();
    };

    const handleDialogClose = () => {
        setVersionsDialogOpen(false);
        setDetailsDialogOpen(false);
    };

    const openDeleteConfirm = () => {
        setDeleteError("");
        setConfirmDeleteOpen(true);
        handleClose();
    };

    const closeDeleteConfirm = () => {
        if (deleting) return;
        setConfirmDeleteOpen(false);
    };

    const handleDeleteProcess = async () => {
        setDeleting(true);
        setDeleteError("");

        try {
            const res = await fetch("api/deleteprocess", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify({ guid: props.row.guid }),
            });

            if (!res.ok) {
                const txt = await res.text().catch(() => "");
                throw new Error(txt || `Delete failed (${res.status})`);
            }

            setConfirmDeleteOpen(false);
            props.setForceRenderCounter(props.forceRenderCounter + 1);
        } catch (e) {
            setDeleteError(e?.message || "Failed to delete process.");
        } finally {
            setDeleting(false);
        }
    };

    const [workerModalOpen, setWorkerModalOpen] = useState(false);
    const [workers, setWorkers] = useState([]);
    const [workersLoading, setWorkersLoading] = useState(false);
    const [workersError, setWorkersError] = useState("");

    const [selectedWorker, setSelectedWorker] = useState(null);
    const [opening, setOpening] = useState(false);
    const [openError, setOpenError] = useState("");

    const isBlank = (s) => !s || String(s).trim() === "";

    const onlineWorkers = useMemo(
        () => (workers || []).filter((m) => m?.is_online === true),
        [workers]
    );

    // 1) Klik: pobierz workery i otwórz modal
    const editInStudio = async () => {
        setWorkersError("");
        setOpenError("");
        setSelectedWorker(null);
        setWorkersLoading(true);

        try {
            const res = await fetch("api/getmachines", {
                headers: { Authorization: "Bearer " + token },
            });

            if (!res.ok) {
                const txt = await res.text().catch(() => "");
                throw new Error(txt || `getmachines failed (${res.status})`);
            }

            const data = await res.json();
            const list = data?.results || [];
            setWorkers(list);

            const online = list.filter((m) => m?.is_online === true);
            if (online.length === 0) {
                setWorkersError("No workers available");
            }

            setWorkerModalOpen(true);
        } catch (e) {
            setWorkersError(e?.message || "Failed to fetch workers.");
            setWorkerModalOpen(true);
        } finally {
            setWorkersLoading(false);
        }
    };

    // 2) Potwierdź w modalu: openinstudio -> window.open
    const confirmOpenInStudio = async () => {
        if (!selectedWorker) {
            setOpenError("Choose worker.");
            return;
        }

        setOpening(true);
        setOpenError("");

        try {
            // 2a) API open
            const res = await fetch("api/skillmapproxy/openinstudio", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify({
                    guid: props.row.guid,
                    machineGuid: selectedWorker.guid, // usuń jeśli backend tego nie przyjmuje
                }),
            });

            if (!res.ok) {
                const txt = await res.text().catch(() => "");
                throw new Error(txt || `Open failed (${res.status})`);
            }

            // 2b) baseUrl z maszyny albo localhost
            const baseUrl = isBlank(selectedWorker?.url)
                ? "http://localhost:5018"
                : `https://${selectedWorker.url}`;

            const draftSegment = `${props.row.name}-${props.row.guid}`;
            const path = `/workflow-editor/Default/${encodeURIComponent(draftSegment)}`;

            window.open(baseUrl + path, "_blank", "noopener,noreferrer");

            setWorkerModalOpen(false);

            // opcjonalnie: refresh listy
            props.setForceRenderCounter?.(props.forceRenderCounter + 1);
        } catch (e) {
            setOpenError(e?.message || "Nie udało się otworzyć w Studio.");
        } finally {
            setOpening(false);
        }
    };
    return (
        <>
            <Tooltip title="Actions">
                <IconButton
                    id="robot-button"
                    aria-controls={open ? "robot-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    size="small"
                    sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Menu
                id="robot-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{ "aria-labelledby": "robot-button" }}
                PaperProps={{
                    sx: { borderRadius: 2, minWidth: 220, overflow: "hidden" },
                }}
            >
                <MenuItem dense onClick={handleVersionsDialogOpen}>
                    <HistoryIcon fontSize="small" style={{ marginRight: 10 }} />
                    <Typography variant="body2">View Versions</Typography>
                </MenuItem>

                <MenuItem dense onClick={handleDetailsDialogOpen}>
                    <InfoOutlinedIcon fontSize="small" style={{ marginRight: 10 }} />
                    <Typography variant="body2">View Details</Typography>
                </MenuItem>
                <MenuItem dense onClick={editInStudio} disabled={workersLoading} sx={{ color: "primary" }}>
                    <EditIcon fontSize="small" style={{ marginRight: 10 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Edit in Studio
                    </Typography>
                </MenuItem>
                <Divider />

                <MenuItem dense onClick={openDeleteConfirm} sx={{ color: "error.main" }}>
                    <DeleteIcon fontSize="small" style={{ marginRight: 10 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Delete Process
                    </Typography>
                </MenuItem>

            </Menu>

            {/* Versions */}
            <Dialog open={versionsDialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    Versions of {props.row.name}
                </DialogTitle>
                <DialogContent dividers>
                    <ProcessVersionsDialog row={props.row} maxversion={props.maxversion} />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleDialogClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Details */}
            <Dialog open={detailsDialogOpen} onClose={handleDialogClose} maxWidth="xl" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    Details of {props.row.name}
                </DialogTitle>
                <DialogContent dividers>
                    <div style={{ width: "100%", height: "80vh" }}>
                        <ReadOnlyWorkflowViewer flowJsonString={props.row.data} />
                    </div>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleDialogClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* ✅ Confirm Delete */}
            <Dialog open={confirmDeleteOpen} onClose={closeDeleteConfirm} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 900 }}>
                    Delete this process?
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                            You’re about to permanently delete:
                        </Typography>

                        <Box
                            sx={{
                                mt: 1,
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: "action.hover",
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {props.row.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                {props.row.guid}
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                            This action cannot be undone.
                        </Typography>

                        {deleteError && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                {deleteError}
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={closeDeleteConfirm} disabled={deleting}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteProcess}
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} thickness={5} /> : <DeleteIcon />}
                    >
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {workerModalOpen && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.45)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                    }}
                    onClick={() => setWorkerModalOpen(false)}
                >
                    <div
                        style={{
                            width: 560,
                            background: "white",
                            borderRadius: 12,
                            padding: 16,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <h3 style={{ margin: 0 }}>Choose worker</h3>
                            <button onClick={() => setWorkerModalOpen(false)} disabled={opening}>
                                ✕
                            </button>
                        </div>

                        <p style={{ marginTop: 8, color: "#555" }}>
                            Choose the worker on which you want to edit this
                        </p>

                        {workersLoading && <p>Loading workers…</p>}

                        {workersError && (
                            <div style={{ padding: 10, background: "#ffecec", borderRadius: 8 }}>
                                {workersError}
                            </div>
                        )}

                        {!workersLoading && !workersError && (
                            <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                                {onlineWorkers.map((m) => (
                                    <label
                                        key={m.id}
                                        style={{
                                            border: "1px solid #ddd",
                                            borderRadius: 10,
                                            padding: 10,
                                            cursor: "pointer",
                                            display: "flex",
                                            gap: 10,
                                            alignItems: "center",
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="worker"
                                            checked={selectedWorker?.id === m.id}
                                            onChange={() => setSelectedWorker(m)}
                                        />
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{m.name || m.id}</div>
                                            <div style={{ fontSize: 12, color: "#666" }}>
                                                URL: {isBlank(m.url) ? "http://localhost:5018" : m.url}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}

                        {openError && (
                            <div style={{ marginTop: 10, padding: 10, background: "#fff6d6", borderRadius: 8 }}>
                                {openError}
                            </div>
                        )}

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                            <button onClick={() => setWorkerModalOpen(false)} disabled={opening}>
                                Cancel
                            </button>
                            <button onClick={confirmOpenInStudio} disabled={opening || workersLoading}>
                                {opening ? "Opening…" : "Open in Studio"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
